import {
  ElementProps,
  Button as MantineButton,
  ButtonProps as MantineButtonProps,
} from "@mantine/core";
import { ReactNode } from "react";

type MuiCompatVariant = "contained" | "outlined" | "text";

// Sizing/radius reproduce the legacy MUI button overrides (height 2.5rem,
// radius 0.5rem, 1rem font) so migrated screens keep their look.
export type ButtonProps = {
  title: string;
  loading?: boolean;
  /**
   * Accepts the MUI-era names (contained/outlined/text) during the MUI →
   * Mantine migration; call sites are renamed to Mantine variants page by
   * page, after which the compat names will be dropped.
   */
  variant?: MuiCompatVariant | MantineButtonProps["variant"];
  /** MUI-era alias for leftSection (same migration caveat as variant). */
  startIcon?: ReactNode;
  /** MUI-era alias for rightSection (same migration caveat as variant). */
  endIcon?: ReactNode;
} & Omit<MantineButtonProps, "variant"> &
  ElementProps<"button", keyof MantineButtonProps>;

const VARIANT_MAP: Record<MuiCompatVariant, MantineButtonProps["variant"]> = {
  contained: "filled",
  outlined: "outline",
  text: "subtle",
};

const isMuiVariant = (
  variant: ButtonProps["variant"],
): variant is MuiCompatVariant =>
  variant === "contained" || variant === "outlined" || variant === "text";

const Button = ({
  title,
  loading,
  variant = "text",
  startIcon,
  endIcon,
  leftSection,
  rightSection,
  ...rest
}: ButtonProps) => (
  <MantineButton
    variant={isMuiVariant(variant) ? VARIANT_MAP[variant] : variant}
    loading={loading}
    leftSection={leftSection ?? startIcon}
    rightSection={rightSection ?? endIcon}
    h="2.5rem"
    px="1rem"
    fz="1rem"
    fw={500}
    radius="md"
    {...rest}
  >
    {title}
  </MantineButton>
);
export default Button;
